import { createFileRoute } from '@tanstack/react-router'
import ReactMarkdown from 'react-markdown'

export const Route = createFileRoute('/privacy-policy')({
  component: RouteComponent,
})

const privacyPolicyContent = `# Privacy Policy

**Effective Date:** January 15, 2025

**Last Updated:** January 15, 2025

---

## 1. Introduction

Welcome to JeridSchool, a comprehensive school management and timetable generation system operated by NEXT Vision Services ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application, website (jeridschool.com), and related services (collectively, the "Service").

By accessing or using JeridSchool, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.

---

## 2. Company Information

**Company Name:** NEXT Vision Services  
**Country:** Tunisia  
**Contact Email:** info@thenextvision.tech  
**Website:** jeridschool.com

For privacy-related inquiries, please contact us at: **info@thenextvision.tech**

---

## 3. About JeridSchool

JeridSchool is an educational management platform designed for schools, universities, and training centers. Our Service enables educational institutions to:

- Manage student data, timetables, and academic records
- Facilitate communication between administrators, teachers, and students
- Generate and manage class schedules
- Store and organize educational content and resources

Certain features of JeridSchool are restricted to authenticated users with appropriate roles (administrators, teachers, or students), ensuring secure access to sensitive educational information.

---

## 4. Information We Collect

### 4.1 Account Information

When you create an account or use our Service, we collect the following information:

- **Email address** (required for account creation and communication)
- **Full name** (to identify users within the system)
- **Role** (administrator, teacher, or student)
- **School or class affiliation** (to associate users with their educational institution)
- **Account credentials** (passwords are encrypted and never stored in plain text)

### 4.2 Media and Photos

- **Uploaded photos:** Users may upload photos for profile pictures, product images, or other educational content
- **Important:** Photos are only uploaded when you actively choose to upload them. We do not automatically capture or upload photos from your device
- **Camera access:** Our mobile application may request camera permissions solely to allow users to capture photos for upload. Camera access is only used when you explicitly initiate a photo capture action within the app

**We do not collect, process, or store any biometric data or facial recognition information.**

### 4.3 Usage Data and Device Information

To improve our Service and ensure reliability, we collect:

- **Device information:** Device type, operating system, browser type, and version
- **Usage patterns:** Features accessed, time spent on the Service, pages viewed
- **Technical data:** IP address, log files, error reports, and performance metrics
- **Analytics data:** Aggregated usage statistics to improve user experience

### 4.4 Notification Data

- **Notification permissions:** We request notification permissions to send timetable updates, alerts, and important announcements
- **Notification preferences:** Your notification settings and preferences
- **Notification history:** Records of notifications sent to help troubleshoot delivery issues

### 4.5 Educational Data

In the course of providing our Service, we may process:

- **Timetable information:** Class schedules, course assignments, and academic calendars
- **Academic records:** Grades, attendance records, and performance data (as authorized by your institution)
- **Communication records:** Messages and communications sent through the platform

---

## 5. How We Use Your Information

We use the collected information for the following purposes:

### 5.1 Service Provision

- **Authentication and authorization:** To verify your identity and grant appropriate access to features based on your role
- **Timetable management:** To store, generate, and display class schedules and academic information
- **User management:** To maintain user accounts and manage relationships between users and educational institutions
- **Communication:** To facilitate communication between administrators, teachers, and students

### 5.2 Media Management

- **Photo storage:** To store and display photos you choose to upload for profiles, educational content, or other authorized purposes
- **Content delivery:** To serve media content you upload to authorized users within your institution

### 5.3 Service Improvement

- **Performance optimization:** To analyze usage patterns and improve platform reliability and functionality
- **Bug fixes:** To identify and resolve technical issues
- **Feature development:** To understand user needs and develop new features

### 5.4 Legal and Security

- **Compliance:** To comply with applicable laws, regulations, and legal obligations
- **Security:** To detect, prevent, and address fraud, security breaches, and other harmful activities
- **Dispute resolution:** To resolve disputes and enforce our terms of service

---

## 6. Data Sharing and Disclosure

### 6.1 No Sale of Personal Information

**We do not sell, rent, or trade your personal information to third parties for marketing or commercial purposes.**

### 6.2 Educational Institutions

We may share information with:

- **Your school or educational institution:** Information necessary for the institution to manage students, teachers, and administrative functions
- **Authorized administrators:** Institution administrators who need access to manage the platform and user accounts

### 6.3 Service Providers

We may share information with trusted service providers who assist us in operating our Service, including:

- **Cloud hosting providers:** For secure data storage and hosting
- **Analytics providers:** For understanding usage patterns (data is aggregated and anonymized where possible)
- **Email service providers:** For sending notifications and communications
- **Payment processors:** If payment processing is required (transaction data only)

All service providers are contractually obligated to maintain the confidentiality and security of your information and are prohibited from using your data for any purpose other than providing services to us.

### 6.4 Legal Requirements

We may disclose your information if required by law or in response to:

- Valid legal requests, subpoenas, or court orders
- Government investigations or regulatory inquiries
- Protection of our rights, property, or safety, or that of our users

### 6.5 Business Transfers

In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections outlined in this policy.

---

## 7. Data Storage and Security

### 7.1 Security Measures

We implement industry-standard security measures to protect your information:

- **Encryption in transit:** All data transmitted between your device and our servers is encrypted using TLS/SSL protocols
- **Encryption at rest:** Sensitive data stored on our servers is encrypted
- **Secure hosting:** Our servers are hosted in secure data centers that comply with international security standards
- **Access controls:** We use role-based access controls to limit who can access your information
- **Regular security audits:** We conduct regular security assessments and updates

### 7.2 Data Retention

We retain your information for as long as:

- Your account is active
- Necessary to provide our Service
- Required by law or contractual obligations with educational institutions
- Necessary to resolve disputes or enforce our agreements

When you delete your account, we will delete or anonymize your personal information in accordance with applicable law and our retention policies.

### 7.3 International Data Transfers

Your information may be stored and processed on servers located outside Tunisia. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.

---

## 8. Children's Privacy

JeridSchool is designed for use by educational institutions. Accounts for minors (users under 18 years of age) are created and controlled by:

- **School administrators:** Who have authority to create and manage student accounts
- **Educational institutions:** Who are responsible for obtaining necessary parental consent where required by law

We do not knowingly collect personal information directly from children without appropriate institutional oversight. If you are a parent or guardian and believe your child has provided us with personal information without proper authorization, please contact us at **info@thenextvision.tech**.

---

## 9. Your Rights and Choices

You have the following rights regarding your personal information:

### 9.1 Access

You have the right to access and review the personal information we hold about you. You can access much of this information through your account settings.

### 9.2 Update and Correction

You can update your personal information at any time through your account settings or by contacting us.

### 9.3 Deletion

You may request deletion of your personal information by:

- Deleting your account through the account settings
- Contacting us at **info@thenextvision.tech**

Note: Some information may be retained as required by law or for legitimate business purposes, such as maintaining academic records for your institution.

### 9.4 Data Export

You can request a copy of your personal data in a portable format by contacting us at **info@thenextvision.tech**.

### 9.5 Permission Withdrawal

You can withdraw permissions at any time:

- **Camera access:** Revoke camera permissions through your device settings
- **Notifications:** Manage notification preferences through the app settings or your device settings
- **Location:** If location services are used, you can revoke access through device settings

### 9.6 Account Deletion

You may delete your account at any time. Account deletion will:

- Remove your personal information from active use
- Retain anonymized data where required for legal or business purposes
- Notify your educational institution if you are a student or teacher

---

## 10. Cookies and Tracking Technologies

We use cookies and similar tracking technologies to:

- Maintain your login session
- Remember your preferences
- Analyze Service usage
- Improve user experience

You can control cookies through your browser settings. Note that disabling cookies may affect certain features of the Service.

---

## 11. Third-Party Links and Services

Our Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.

---

## 12. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of any material changes by:

- Posting the new Privacy Policy on this page
- Updating the "Last Updated" date
- Sending an email notification to registered users (for significant changes)
- Displaying a notice within the Service

Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.

---

## 13. Compliance

### 13.1 Google Play User Data Policy

JeridSchool complies with Google Play's User Data Policy requirements, including:

- Transparent disclosure of data collection and use
- Secure handling of user data
- User control over their data
- No sale of personal information

### 13.2 GDPR Compliance

For users in the European Economic Area (EEA), we comply with the General Data Protection Regulation (GDPR), including:

- Lawful basis for processing personal data
- Data subject rights (access, rectification, erasure, portability, etc.)
- Data protection by design and by default
- Appropriate safeguards for international transfers

### 13.3 Tunisian Regulations

We comply with applicable Tunisian data protection laws and regulations, including:

- Law No. 2004-63 on the Protection of Personal Data
- Any other applicable local regulations

---

## 14. Biometric and Facial Recognition Data

**Important Statement:** JeridSchool does not collect, process, or store any biometric data or facial recognition information. We do not use facial recognition technology, fingerprint scanning, or any other biometric identification methods.

Photos uploaded to our Service are stored as regular image files and are not processed for biometric identification purposes.

---

## 15. Photo Upload Policy

**User-Initiated Photo Uploads Only:** 

Photos are only uploaded to our Service when you actively choose to upload them. We do not:

- Automatically capture photos from your device
- Access your photo gallery without your explicit permission
- Upload photos in the background
- Use your camera without your knowledge

When you choose to upload a photo:

- You must explicitly select the photo or capture a new one
- You will see clear confirmation that the upload is in progress
- You can cancel the upload at any time before it completes

---

## 16. Contact Us

If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**Email:** info@thenextvision.tech

**Company:** NEXT Vision Services  
**Country:** Tunisia

We will respond to your inquiry within a reasonable timeframe and in accordance with applicable law.

---

## 17. Data Protection Officer

For privacy-related matters, especially those involving GDPR or other regulatory compliance, you can reach our data protection team at:

**Email:** info@thenextvision.tech

---

## 18. Governing Law

This Privacy Policy is governed by the laws of Tunisia. Any disputes arising from this Privacy Policy will be resolved in accordance with Tunisian law and jurisdiction.

---

## 19. Consent

By using JeridSchool, you consent to:

- The collection and use of information as described in this Privacy Policy
- The processing of your data in Tunisia and other jurisdictions where our service providers operate
- The transfer of your information to third-party service providers as described in this policy

---

## 20. Acknowledgment

You acknowledge that you have read and understood this Privacy Policy. If you do not agree with any part of this policy, you must discontinue use of the Service immediately.

---

**Thank you for using JeridSchool. We are committed to protecting your privacy and providing a secure educational management platform.**

---

*This Privacy Policy is effective as of January 15, 2025. Please review this policy periodically for updates.*`

function RouteComponent() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 md:p-12">
          <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-h1:text-4xl prose-h1:mb-6 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-hr:border-gray-300 dark:prose-hr:border-gray-600">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4 pt-4 border-t border-gray-200 dark:border-gray-700 first:border-t-0 first:pt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-gray-900 dark:text-gray-100">
                    {children}
                  </strong>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 mb-4 space-y-2 text-gray-700 dark:text-gray-300">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {children}
                  </li>
                ),
                hr: () => (
                  <hr className="my-8 border-gray-300 dark:border-gray-600" />
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={
                      href?.startsWith('http')
                        ? 'noopener noreferrer'
                        : undefined
                    }
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {privacyPolicyContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  )
}

